import React from 'react';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/admin/components/ui/dialog';
import { Input } from '@/admin/components/ui/input';
import { Label } from '@/admin/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  createAdminAddon,
  fetchAdminAddons,
  updateAdminAddon,
  type AdminAddonPlan,
} from '@/admin/lib/api';

const creditFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const formatCredits = (value: number) => {
  if (!Number.isFinite(value)) return '0';
  return creditFormatter.format(value);
};

const formatCurrency = (amountCents: number, currency: string) => {
  if (!Number.isFinite(amountCents)) return '0';
  if (amountCents === 0) return 'Free';
  const normalizedCurrency = currency ? currency.toUpperCase() : 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 2,
    }).format(amountCents / 100);
  } catch {
    return `$${(amountCents / 100).toFixed(2)}`;
  }
};

type AddonFormValues = {
  planCode: string;
  creditsGrant: string;
  currency: string;
  amountCents: string;
  displayCopy: string;
};

type AddonFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: AddonFormValues) => Promise<void>;
  title: string;
  description: string;
  initialValues: AddonFormValues;
  planCodeReadOnly?: boolean;
};

function AddonFormDialog({
  open,
  onOpenChange,
  onSubmit,
  title,
  description,
  initialValues,
  planCodeReadOnly,
}: AddonFormDialogProps) {
  const [values, setValues] = React.useState<AddonFormValues>(initialValues);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setValues(initialValues);
  }, [initialValues, open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="plan_code">Plan code</Label>
            <Input
              id="plan_code"
              value={values.planCode}
              onChange={event =>
                setValues(prev => ({ ...prev, planCode: event.target.value }))
              }
              placeholder="addon_custom"
              disabled={planCodeReadOnly}
              required
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="credits_grant">Credits grant</Label>
              <Input
                id="credits_grant"
                type="number"
                min={1}
                value={values.creditsGrant}
                onChange={event =>
                  setValues(prev => ({
                    ...prev,
                    creditsGrant: event.target.value,
                  }))
                }
                placeholder="4000000"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount_cents">Amount (cents)</Label>
              <Input
                id="amount_cents"
                type="number"
                min={0}
                value={values.amountCents}
                onChange={event =>
                  setValues(prev => ({
                    ...prev,
                    amountCents: event.target.value,
                  }))
                }
                placeholder="499"
                required
              />
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Input
                id="currency"
                value={values.currency}
                onChange={event =>
                  setValues(prev => ({ ...prev, currency: event.target.value }))
                }
                placeholder="USD"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="display_copy">Display copy</Label>
              <Input
                id="display_copy"
                value={values.displayCopy}
                onChange={event =>
                  setValues(prev => ({
                    ...prev,
                    displayCopy: event.target.value,
                  }))
                }
                placeholder="$4.99 for 4M credits"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export const AddonsPage: React.FC = () => {
  const [addons, setAddons] = React.useState<AdminAddonPlan[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editAddon, setEditAddon] = React.useState<AdminAddonPlan | null>(null);

  const loadAddons = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAdminAddons();
      setAddons(response.addons);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load addons');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadAddons();
  }, [loadAddons]);

  const handleCreateAddon = async (values: AddonFormValues) => {
    const creditsGrant = Number(values.creditsGrant);
    const amountCents = Number(values.amountCents);
    if (!Number.isFinite(creditsGrant) || creditsGrant <= 0) {
      throw new Error('Credits grant must be positive.');
    }
    if (!Number.isFinite(amountCents) || amountCents < 0) {
      throw new Error('Amount must be non-negative.');
    }
    await createAdminAddon({
      plan_code: values.planCode.trim(),
      credits_grant: creditsGrant,
      currency: values.currency.trim(),
      amount_cents: amountCents,
      display_copy: values.displayCopy.trim() || null,
    });
    await loadAddons();
  };

  const handleUpdateAddon = async (values: AddonFormValues) => {
    if (!editAddon) return;
    const creditsGrant = Number(values.creditsGrant);
    const amountCents = Number(values.amountCents);
    if (!Number.isFinite(creditsGrant) || creditsGrant <= 0) {
      throw new Error('Credits grant must be positive.');
    }
    if (!Number.isFinite(amountCents) || amountCents < 0) {
      throw new Error('Amount must be non-negative.');
    }
    await updateAdminAddon(editAddon.plan_code, {
      credits_grant: creditsGrant,
      currency: values.currency.trim(),
      amount_cents: amountCents,
      display_copy: values.displayCopy.trim() || null,
    });
    await loadAddons();
  };

  const createDefaults: AddonFormValues = {
    planCode: '',
    creditsGrant: '',
    currency: 'USD',
    amountCents: '',
    displayCopy: '',
  };

  const editDefaults: AddonFormValues = editAddon
    ? {
        planCode: editAddon.plan_code,
        creditsGrant: `${editAddon.credits_grant}`,
        currency: editAddon.currency,
        amountCents: `${editAddon.amount_cents}`,
        displayCopy: editAddon.display_copy ?? '',
      }
    : createDefaults;

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-auto py-4 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Add-ons</h1>
            <p className="text-sm text-muted-foreground">
              Manage redeem-only add-ons and view system add-ons.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadAddons}>
              Refresh
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              New add-on
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="px-4 lg:px-6">
          <Card className="border-destructive/40">
            <CardHeader>
              <CardTitle className="text-base">
                Unable to load add-ons
              </CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      ) : null}

      <div className="px-4 lg:px-6">
        <Card>
          <CardHeader>
            <CardTitle>Available add-ons</CardTitle>
            <CardDescription>
              System add-ons are purchasable; admin add-ons are redeem-only.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Display copy</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {addons.map(addon => (
                  <TableRow key={addon.plan_code}>
                    <TableCell className="font-medium">
                      {addon.plan_code}
                    </TableCell>
                    <TableCell>{formatCredits(addon.credits_grant)}</TableCell>
                    <TableCell>
                      {formatCurrency(addon.amount_cents, addon.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{addon.addon_source}</Badge>
                    </TableCell>
                    <TableCell>{addon.display_copy ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      {addon.addon_source === 'admin' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditAddon(addon)}
                        >
                          Edit
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" disabled>
                          Read-only
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && addons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm">
                      No add-ons found.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <AddonFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreateAddon}
        title="New add-on"
        description="Create a redeem-only add-on plan."
        initialValues={createDefaults}
      />

      <AddonFormDialog
        open={Boolean(editAddon)}
        onOpenChange={open => {
          if (!open) setEditAddon(null);
        }}
        onSubmit={handleUpdateAddon}
        title="Edit add-on"
        description="Update credits and pricing for this add-on."
        initialValues={editDefaults}
        planCodeReadOnly
      />
    </div>
  );
};
